// src/components/configuracoes/notificacoes/NotificacoesConfigPage.js
import React, { Fragment, useState, useCallback, useMemo } from 'react';
import { Bell, Filter as FilterIcon, CheckCheck, Trash2, Send, Loader2, XCircle } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import Pagination from '../../common/Pagination';
import {
    useNotificacoes,
    useEstatisticasNotificacoes,
    useMutateNotificacoes,
} from '../../../hooks/useNotificacoes';
import { useAuth } from '../../../context/auth/AuthProvider';
import { FormStepper, FormStepNavigation } from '../../shared/FormStepper';

const STEP_LABELS = ['Enviar Notificação'];

const TIPOS = ['sistema', 'pagamento', 'agendamento', 'atualizacao', 'alerta', 'promocao', 'lembrete', 'seguranca'];

const TIPO_LABEL = {
    sistema: 'Sistema', pagamento: 'Pagamento', agendamento: 'Agendamento',
    atualizacao: 'Atualização', alerta: 'Alerta', promocao: 'Promoção',
    lembrete: 'Lembrete', seguranca: 'Segurança',
};

const PRIORIDADE_BADGE = {
    baixa: 'badge-success', media: 'badge-warning', alta: 'badge-danger', urgente: 'badge-danger',
};

const TABS = { lista: 'lista', enviar: 'enviar' };

const NotificacoesConfigPage = () => {
    const { user } = useAuth();
    const [tab, setTab] = useState(TABS.lista);
    const [currentPage, setCurrentPage] = useState(1);
    const [filtros, setFiltros] = useState({ tipo: '', lida: '' });
    const itemsPerPage = 15;

    // Form de envio em massa
    const [form, setForm] = useState({
        titulo: '', mensagem: '', tipo: 'sistema', link: '', icone: '', usuariosInput: '',
    });

    const queryParams = useMemo(() => ({
        page: currentPage,
        limit: itemsPerPage,
        ...(filtros.tipo && { tipo: filtros.tipo }),
        ...(filtros.lida !== '' && { lida: filtros.lida }),
    }), [currentPage, filtros, itemsPerPage]);

    const { data: notifData, isLoading } = useNotificacoes(queryParams);
    const { data: stats, isLoading: statsLoading } = useEstatisticasNotificacoes();
    const { marcarTodasLidas, deletarTodas, enviarMultiplas } = useMutateNotificacoes();

    const notificacoes = notifData?.data || [];
    const total = Number(notifData?.count) || 0;

    const handleFiltroChange = useCallback((key, value) => {
        setFiltros(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    }, []);

    const handleMarcarTodas = useCallback(() => {
        if (window.confirm('Marcar todas as notificações como lidas?')) {
            marcarTodasLidas.mutate();
        }
    }, [marcarTodasLidas]);

    const handleDeletarTodas = useCallback(() => {
        if (window.confirm('Tem certeza que deseja excluir TODAS as notificações? Esta ação não pode ser desfeita.')) {
            deletarTodas.mutate();
        }
    }, [deletarTodas]);

    const handleFormChange = useCallback((e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleEnviar = useCallback((e) => {
        e.preventDefault();
        const usuarios = form.usuariosInput
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
        if (!usuarios.length) return;
        enviarMultiplas.mutate({
            usuarios,
            titulo: form.titulo,
            mensagem: form.mensagem,
            tipo: form.tipo,
            link: form.link || undefined,
            icone: form.icone || undefined,
        }, {
            onSuccess: () => setForm({ titulo: '', mensagem: '', tipo: 'sistema', link: '', icone: '', usuariosInput: '' }),
        });
    }, [form, enviarMultiplas]);

    const statsData = typeof stats?.total === 'object' ? stats.total : stats;
    const statsCards = [
        { label: 'Total', value: statsData?.total ?? '—', color: 'var(--color-primary)' },
        { label: 'Não Lidas', value: statsData?.totalNaoLidas ?? statsData?.naoLidas ?? '—', color: 'var(--color-warning)' },
        { label: 'Lidas', value: statsData?.totalLidas ?? statsData?.lidas ?? '—', color: 'var(--color-success)' },
    ];

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Bell />}
                    title="Notificações"
                    subtitle={!isLoading && total > 0 ? `${total} notificação${total !== 1 ? 'ões' : ''}` : null}
                    actions={<>
                        {tab === TABS.lista && (
                            <>
                                <button
                                    onClick={handleMarcarTodas}
                                    className="action-button action-button-secondary"
                                    disabled={marcarTodasLidas.isPending || isLoading}
                                >
                                    <CheckCheck size={18} className="icon"/>
                                    Marcar todas lidas
                                </button>
                                <button
                                    onClick={handleDeletarTodas}
                                    className="action-button action-button-secondary"
                                    disabled={deletarTodas.isPending || isLoading}
                                >
                                    <Trash2 size={18} className="icon"/>
                                    Excluir todas
                                </button>
                            </>
                        )}
                        <button
                            onClick={() => setTab(tab === TABS.enviar ? TABS.lista : TABS.enviar)}
                            className={`action-button ${tab === TABS.enviar ? 'action-button-primary' : 'action-button-secondary'}`}
                        >
                            {tab === TABS.enviar ? <XCircle size={18} className="icon"/> : <Send size={18} className="icon"/>}
                            {tab === TABS.enviar ? 'Fechar' : 'Enviar Mensagem'}
                        </button>
                    </>}
                />

                {/* Cards de Estatísticas */}
                {!statsLoading && stats && (
                    <div className="dashboard-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '1rem' }}>
                        {statsCards.map(card => (
                            <div key={card.label} className="kpi-card">
                                <h5 className="kpi-card__title">{card.label}</h5>
                                <p className="kpi-card__value" style={{ color: card.color }}>{card.value}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* Formulário de envio em massa */}
                {tab === TABS.enviar && (
                    <div className="content-panel">
                        <FormStepper currentStep={1} steps={STEP_LABELS} />
                        <form onSubmit={handleEnviar}>
                            <div className="form-section">
                                <h4 className="form-section-title">Enviar Notificação em Massa</h4>
                            </div>
                            <div className="form-grid-container-cols-2">
                                <div className="form-group">
                                    <label className="form-label required">Título</label>
                                    <input
                                        name="titulo"
                                        value={form.titulo}
                                        onChange={handleFormChange}
                                        className="form-input"
                                        required
                                        maxLength={120}
                                        placeholder="Título da notificação"
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label required">Tipo</label>
                                    <select name="tipo" value={form.tipo} onChange={handleFormChange} className="form-input">
                                        {TIPOS.map(t => <option key={t} value={t}>{TIPO_LABEL[t]}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label required">Mensagem</label>
                                <textarea
                                    name="mensagem"
                                    value={form.mensagem}
                                    onChange={handleFormChange}
                                    className="form-input"
                                    rows={3}
                                    required
                                    maxLength={500}
                                    placeholder="Texto da notificação"
                                />
                            </div>
                            <div className="form-grid-container-cols-2">
                                <div className="form-group">
                                    <label className="form-label">Link (opcional)</label>
                                    <input name="link" value={form.link} onChange={handleFormChange} className="form-input" placeholder="/rota/interna"/>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Ícone (opcional)</label>
                                    <input name="icone" value={form.icone} onChange={handleFormChange} className="form-input" placeholder="bell"/>
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label required">IDs dos Usuários (separados por vírgula)</label>
                                <textarea
                                    name="usuariosInput"
                                    value={form.usuariosInput}
                                    onChange={handleFormChange}
                                    className="form-input"
                                    rows={2}
                                    required
                                    placeholder="userId1, userId2, userId3..."
                                />
                            </div>
                            <div className="form-actions">
                                <button type="button" onClick={() => setTab(TABS.lista)} className="action-button action-button-secondary">
                                    <XCircle size={18} className="icon" />Cancelar
                                </button>
                                <FormStepNavigation
                                    currentStep={1}
                                    totalSteps={1}
                                    onBack={() => {}}
                                    onNext={() => {}}
                                    isSubmitting={enviarMultiplas.isPending}
                                    submitLabel="Enviar Notificação"
                                />
                            </div>
                        </form>
                    </div>
                )}

                {/* Painel de filtros */}
                {tab === TABS.lista && (
                    <div className="content-panel" style={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0.75rem 1.25rem' }}>
                        <FilterIcon size={16} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }}/>
                        <div className="form-group" style={{ margin: 0, minWidth: 160 }}>
                            <select
                                value={filtros.tipo}
                                onChange={e => handleFiltroChange('tipo', e.target.value)}
                                className="form-input"
                                style={{ padding: '0.375rem 0.625rem' }}
                            >
                                <option value="">Todos os tipos</option>
                                {TIPOS.map(t => <option key={t} value={t}>{TIPO_LABEL[t]}</option>)}
                            </select>
                        </div>
                        <div className="form-group" style={{ margin: 0, minWidth: 140 }}>
                            <select
                                value={filtros.lida}
                                onChange={e => handleFiltroChange('lida', e.target.value)}
                                className="form-input"
                                style={{ padding: '0.375rem 0.625rem' }}
                            >
                                <option value="">Todas</option>
                                <option value="false">Não lidas</option>
                                <option value="true">Lidas</option>
                            </select>
                        </div>
                        {(filtros.tipo || filtros.lida !== '') && (
                            <button
                                onClick={() => { setFiltros({ tipo: '', lida: '' }); setCurrentPage(1); }}
                                className="action-button action-button-secondary"
                                style={{ padding: '0.375rem 0.75rem' }}
                            >
                                <XCircle size={14} className="icon"/> Limpar
                            </button>
                        )}
                    </div>
                )}
            </div>

            {tab === TABS.lista && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin"/>
                                <span>Carregando notificações...</span>
                            </div>
                        ) : notificacoes.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 8 }}></th>
                                        <th style={{ textAlign: 'left' }}>Título</th>
                                        <th>Tipo</th>
                                        <th>Prioridade</th>
                                        <th>Data</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {notificacoes.map(n => (
                                        <tr key={n._id} style={{ opacity: n.lida ? 0.6 : 1 }}>
                                            <td style={{ padding: '0 0 0 1rem' }}>
                                                {!n.lida && (
                                                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--color-primary)' }}/>
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'left' }}>
                                                <div style={{ fontWeight: n.lida ? 400 : 600, fontSize: '0.875rem' }}>{n.titulo}</div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>{n.mensagem}</div>
                                            </td>
                                            <td><span className="badge badge-info">{TIPO_LABEL[n.tipo] || n.tipo}</span></td>
                                            <td>
                                                {n.metadata?.priority && (
                                                    <span className={`badge ${PRIORIDADE_BADGE[n.metadata.priority] || 'badge-info'}`}>
                                                        {n.metadata.priority}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                                                {n.createdAt ? new Date(n.createdAt).toLocaleDateString('pt-BR') : '—'}
                                            </td>
                                            <td>
                                                <span className={`badge ${n.lida ? 'badge-success' : 'badge-warning'}`}>
                                                    {n.lida ? 'Lida' : 'Não lida'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <Bell size={48}/>
                                <h3>Nenhuma notificação encontrada</h3>
                                <p>{filtros.tipo || filtros.lida !== '' ? 'Tente ajustar os filtros.' : 'Não há notificações no momento.'}</p>
                            </div>
                        )}
                    </div>
                    <Pagination
                        totalItems={total}
                        itemsPerPage={itemsPerPage}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                    />
                </div>
            )}
        </Fragment>
    );
};

export default NotificacoesConfigPage;
